import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { pl } from './locales/pl'
import { en } from './locales/en'
import type { Language, Dictionary } from './types'

const dictionaries: Record<Language, Dictionary> = { pl, en }
const STORAGE_KEY = 'solidlabs-language'

interface LanguageContextValue {
  language: Language
  setLanguage: (lang: Language) => void
  t: Dictionary
}

const LanguageContext = createContext<LanguageContextValue | null>(null)

/**
 * Detect the reader's language preference.
 * Priority:
 *   1. localStorage (user's previously-set choice)
 *   2. navigator.language ('pl*' → PL, else EN)
 *   3. 'pl' fallback
 */
function detectLanguage(): Language {
  if (typeof window === 'undefined') return 'pl'

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    if (stored === 'pl' || stored === 'en') return stored
  } catch {
    // localStorage may be disabled (private mode, strict permissions)
  }

  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language.toLowerCase().startsWith('pl') ? 'pl' : 'en'
  }

  return 'pl'
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  // Pierwsze renderowanie zawsze po polsku — tak samo w przeglądarce jak
  // w prerenderze przy buildzie. Gdyby stan startowy zależał od localStorage,
  // hydratacja nie zgadzałaby się z gotowym HTML-em i React przerysowywałby
  // całe drzewo. Wykrycie języka wchodzi zaraz po hydratacji, w efekcie.
  const [language, setLanguageState] = useState<Language>('pl')

  useEffect(() => {
    const detected = detectLanguage()
    if (detected !== 'pl') setLanguageState(detected)
  }, [])

  // Atrybut lang na <html> — jedyna rzecz zależna wyłącznie od języka.
  // Tytuł i opis strony ustawia aktywna trasa przez useDocumentMeta: są różne
  // dla landingu, listy bloga i pojedynczego artykułu, więc nie mogą być
  // przypisane globalnie do języka.
  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    try {
      window.localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // localStorage may be disabled — switch still works in-memory.
    }
  }

  return (
    <LanguageContext.Provider
      value={{ language, setLanguage, t: dictionaries[language] }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

/** Zwraca aktywny słownik. Użycie: `const t = useT(); t.nav.about` */
export function useT(): Dictionary {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useT must be used within LanguageProvider')
  return ctx.t
}

/** Zwraca aktualny język i setter — do przełącznika PL/EN. */
export function useLanguage() {
  const ctx = useContext(LanguageContext)
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider')
  return { language: ctx.language, setLanguage: ctx.setLanguage }
}
